import React, { useEffect, useRef, useState, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getReportClassList } from "hooks/useFilterPersistence/useFilterPersistence";
import styled from "styled-components";
import gsap from "gsap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faStar,
  faSpinner,
  faArrowUp,
} from "@fortawesome/free-solid-svg-icons";
import { useHistory } from "react-router-dom";
import { colors, constants } from "utils";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, logApi, LogCategory, LogLevel } from "utils/sentryLogger";
import { BetaTag } from "components/shared/BetaTag/BetaTag";
import { saveFilters, resolveClassId } from "utils/persistFilters";
import { FeedbackCTA } from "components/shared/FeedbackCTA/FeedbackCTA";
import { DateFilterDropdown, SelectDropdown } from "components/shared/FilterDropdowns/FilterDropdowns";
import { classDisplayName } from "components/shared/ClassroomFilter/groupClassesBySchoolYear";
import { trackClickedButton } from "utils/analytics";
import { FeedbackOverlay } from "./FeedbackOverlay";
import { ProficiencyLevelBadge } from "../Proficiency/ProficiencyLevelBadge";
import { STATUS_CONFIG } from "../Proficiency/ProficiencyCell";
import { Bronze } from "components/shared/Badges/Frame/Bronze";
import { Silver } from "components/shared/Badges/Frame/Silver";
import { Gold } from "components/shared/Badges/Frame/Gold";
import { Diamond } from "components/shared/Badges/Frame/Diamond";
import iconStudents from "assets/icons/icon-students.png";
import iconSessionsCompleted from "assets/icons/icon-sessions-completed.png";
import iconDosageGoal from "assets/icons/icon-dosage-goal.png";
import { pageTitleInset } from "components/pageTitleInset";

// The UI calls this page "Class Insights", but Mixpanel has always recorded it
// as "Weekly Reports". Keep it that way so its history stays continuous.
const WEEKLY_REPORTS_PAGE_NAME = "Weekly Reports";

// One key builder for the report query and the feedback flow's optimistic
// cache writes, so they can never target different entries.
const reportKey = (classId, week, endDate, dateMode, educatorId) =>
  ["weekly-report", classId, week, endDate ?? null, dateMode ?? null, educatorId ?? null];

const DurationUnit = styled.span`
  font-size: 1rem;
`;

const formatDuration = (totalMinutes) => {
  if (totalMinutes == null) return <>0<DurationUnit>min</DurationUnit></>;
  if (totalMinutes < 60) return <>{totalMinutes}<DurationUnit>min</DurationUnit></>;
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (mins === 0) return <>{hours}<DurationUnit>hr</DurationUnit></>;
  return <>{hours}<DurationUnit>hr</DurationUnit> {mins}<DurationUnit>min</DurationUnit></>;
};

const formatWeekDate = (isoDate) => {
  const date = new Date(isoDate + "T00:00:00");
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const WeeklyReport = ({ educatorId = null, sharedClassId, onClassChange }) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [classes, setClasses] = useState([]);
  const [selectedClassIdInternal, setSelectedClassIdInternal] = useState(null);

  const selectedClassId = selectedClassIdInternal;
  const setSelectedClassId = (id) => {
    setSelectedClassIdInternal(id);
    if (onClassChange) onClassChange(id);
  };
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [selectedDateMode, setSelectedDateMode] = useState("weekly");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedbackOverlay, setFeedbackOverlay] = useState({ show: false, section: null, initialRating: null });
  const [isDownloading, setIsDownloading] = useState(false);
  const reportRef = useRef(null);

  const selectedClass = classes.find((c) => c.id === selectedClassId);

  useEffect(() => {
    if (selectedClassId) {
      saveFilters("weekly_report", {
        selectedClassId,
        selectedWeek,
        selectedEndDate,
        selectedDateMode,
      });
    }
  }, [selectedClassId, selectedWeek, selectedEndDate, selectedDateMode]);

  // Fetch educator data to get classes
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const classList = await getReportClassList(queryClient, educatorId);
        setClasses(classList);
        if (classList.length > 0) {
          if (sharedClassId && classList.some(c => c.id === sharedClassId)) {
            setSelectedClassId(sharedClassId);
          } else {
            const { classId, saved } = await resolveClassId("weekly_report", classList);
            if (saved?.selectedWeek) {
              setSelectedWeek(saved.selectedWeek);
              if (saved.selectedEndDate) setSelectedEndDate(saved.selectedEndDate);
              if (saved.selectedDateMode) setSelectedDateMode(saved.selectedDateMode);
            }
            setSelectedClassId(classId);
          }
        }
      } catch (e) {
        const errorMessage = e?.detail || e?.message || "An unknown error occurred.";
        logError("Failed to fetch class data", e, {
          category: LogCategory.ERROR,
        });
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    fetchClasses();
  }, [educatorId, queryClient]);

  const handleDatesLoaded = useCallback((weeks, days) => {
    setSelectedWeek((prev) => {
      // Keep the current selection only if it actually exists in the dates we
      // just loaded. A stale week restored from saved filters, or a week that
      // belonged to a previously selected class, won't be present here — in
      // that case fall through and pick the most recent available date so the
      // report loads on first render instead of waiting for a manual filter
      // change.
      const weekExists = prev && weeks.some((w) => w.week_of === prev);
      const dayExists = prev && days.some((d) => d.date === prev);
      if (weekExists || dayExists) return prev;
      if (weeks.length > 0) {
        setSelectedDateMode("weekly");
        return weeks[0].week_of;
      }
      if (days.length > 0) {
        setSelectedDateMode("daily");
        return days[0].date;
      }
      return null;
    });
  }, []);

  // The report, cached per (class, date selection): a revisited selection paints
  // instantly while revalidating in the background; the spinner only covers an
  // uncached one (OKO2-3684). A 404 is an ordinary empty week, not an error.
  const reportQueryKey = reportKey(selectedClassId, selectedWeek, selectedEndDate, selectedDateMode, educatorId);
  const reportQuery = useQuery({
    queryKey: reportQueryKey,
    queryFn: async () => {
      const reportParams = new URLSearchParams();
      if (educatorId) reportParams.set("educator_id", educatorId);
      if (selectedDateMode === "daily") {
        reportParams.set("start_date", selectedWeek);
        const nextDay = new Date(selectedWeek + "T00:00:00");
        nextDay.setDate(nextDay.getDate() + 1);
        reportParams.set("end_date", nextDay.toISOString().split("T")[0]);
      } else if (selectedDateMode === "custom" && selectedEndDate) {
        reportParams.set("start_date", selectedWeek);
        reportParams.set("end_date", selectedEndDate);
      } else {
        reportParams.set("week_of", selectedWeek);
      }
      try {
        return await request(
          `class-report/${selectedClassId}/weekly-report?${reportParams.toString()}`,
          { method: "GET" }
        );
      } catch (e) {
        if (e?.status === 404) return null;
        throw e;
      }
    },
    enabled: Boolean(selectedClassId && selectedWeek),
    staleTime: 0,
  });
  const weeklyReport = reportQuery.data ?? null;
  const reportLoading = reportQuery.isInitialLoading;

  // The old fetch set/cleared the error inline; the query reports it as state,
  // so mirror that into the page's error channel. A disabled query must not
  // clear an error another flow set.
  useEffect(() => {
    const e = reportQuery.error;
    if (!e) {
      if (reportQuery.isFetched) setError(null);
      return;
    }
    if (e?.status === 403) {
      setError("You don't have access to this classroom.");
    } else {
      logError("Failed to fetch weekly report", e, {
        category: LogCategory.ERROR,
      });
      setError(e?.detail || e?.message || "Failed to load weekly report.");
    }
  }, [reportQuery.error, reportQuery.isFetched]);

  const handleFeedback = useCallback(async (section, isPositive, comment = null) => {
    if (!selectedClassId || !selectedWeek) return;
    const key = reportKey(selectedClassId, selectedWeek, selectedEndDate, selectedDateMode, educatorId);

    // Abort any in-flight background revalidation first, so its response can't
    // land on top of the optimistic write below.
    await queryClient.cancelQueries({ queryKey: key });

    // Save previous state for rollback
    const previousFeedback = weeklyReport?.section_feedback?.[section];

    // Optimistic update, written into the cache so the report and its
    // background refetches stay one source of truth.
    queryClient.setQueryData(key, (prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        section_feedback: {
          ...prev.section_feedback,
          [section]: { section, is_positive: isPositive, comment },
        },
      };
    });

    try {
      const result = await request(
        `class-report/${selectedClassId}/weekly-report/feedback`,
        {
          method: "POST",
          payload: {
            section,
            is_positive: isPositive,
            comment,
            week_of: selectedWeek,
          },
        }
      );
      // Sync with server response
      queryClient.setQueryData(key, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          section_feedback: {
            ...prev.section_feedback,
            [section]: result,
          },
        };
      });
    } catch (e) {
      // Revert to previous state
      queryClient.setQueryData(key, (prev) => {
        if (!prev) return prev;
        const reverted = { ...prev.section_feedback };
        if (previousFeedback) {
          reverted[section] = previousFeedback;
        } else {
          delete reverted[section];
        }
        return { ...prev, section_feedback: reverted };
      });
      logApi("Failed to submit section feedback", {
        level: LogLevel.WARNING,
        errorMessage: e?.message,
        errorStatus: e?.status,
        section,
      });
    }
  }, [selectedClassId, selectedWeek, selectedEndDate, selectedDateMode, educatorId, weeklyReport, queryClient]);

  const weekEnd = weeklyReport?.end_date || weeklyReport?.week_end || null;
  const weekOf = weeklyReport?.start_date || selectedWeek;

  const navigateToStudentActivity = useCallback(() => {
    saveFilters("student_activity", {
      selectedClassId,
      startDate: weekOf,
      endDate: weekEnd,
    });
    window.open(`${constants.CLASS_REPORTS_PATH}?tab=dosage`, "_blank");
  }, [selectedClassId, weekOf, weekEnd]);

  const SECTION_LABELS = {
    activity_summary: "Dosage Summary",
    student_stars: "Student Stars",
    collaboration: "Collaboration",
    proficiency: "Math Proficiency",
  };

  const openFeedbackOverlay = useCallback((section, isPositive) => {
    trackClickedButton("Section Feedback", {
      page_name: WEEKLY_REPORTS_PAGE_NAME,
      section_name: section,
      rating: isPositive ? "Positive" : "Negative",
    });
    setFeedbackOverlay({ show: true, section, initialRating: isPositive });
  }, []);

  const handleDownloadPdf = useCallback(async () => {
    trackClickedButton("Download PDF", {
      page_name: WEEKLY_REPORTS_PAGE_NAME,
      class: selectedClassId,
      class_name: selectedClass?.name,
      week_of: weekOf,
    });
    if (!reportRef.current || isDownloading) return;
    setIsDownloading(true);

    try {
      const element = reportRef.current;

      // Hide elements that should not appear in the PDF
      const hiddenSelectors = [
        "[data-pdf-exclude]",
      ];
      const hiddenElements = [];
      hiddenSelectors.forEach((selector) => {
        element.querySelectorAll(selector).forEach((el) => {
          hiddenElements.push({ el, display: el.style.display });
          el.style.display = "none";
        });
      });

      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#F5F3EF",
      });

      // Restore hidden elements
      hiddenElements.forEach(({ el, display }) => {
        el.style.display = display;
      });

      const imgData = canvas.toDataURL("image/png");
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      // A4 dimensions in points
      const pdfWidth = 595.28;
      const pdfHeight = 841.89;
      const ratio = pdfWidth / imgWidth;
      const scaledHeight = imgHeight * ratio;

      const pdf = new jsPDF("p", "pt", "a4");

      let position = 0;
      let remainingHeight = scaledHeight;

      // Add pages as needed
      while (remainingHeight > 0) {
        if (position > 0) {
          pdf.addPage();
        }
        pdf.addImage(imgData, "PNG", 0, -position, pdfWidth, scaledHeight);
        position += pdfHeight;
        remainingHeight -= pdfHeight;
      }

      const className = selectedClass?.name || "report";
      const weekLabel = selectedWeek || "current";
      pdf.save(`weekly-report-${className}-${weekLabel}.pdf`);
    } catch (e) {
      logError("Failed to download PDF", e, {
        category: LogCategory.ERROR,
      });
    } finally {
      setIsDownloading(false);
    }
  }, [isDownloading, selectedClass, selectedClassId, selectedWeek, weekOf]);

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <FontAwesomeIcon icon={faSpinner} spin size="2x" color={colors.blue500} />
        </LoadingContainer>
      </Container>
    );
  }

  if (error && !weeklyReport) {
    return (
      <Container>
        <PageHeader>
          <HeaderContent>
            <div>
              <TitleRow>
                <Title>{constants.SESSION_HISTORY} {constants.BY_DATE}</Title>
                <BetaTag text="These Reports are in BETA as they are generated by our ever-improving AI and may contain inaccuracies. Use it as a helpful guide to supplement your expert observations." />
              </TitleRow>
            </div>
          </HeaderContent>
        </PageHeader>
        <ErrorContainer>{error}</ErrorContainer>
      </Container>
    );
  }

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 20;
    window.scrollTo({ top, behavior: "smooth" });
  };

  return (
    <Container ref={reportRef}>
      <PageHeader>
        <HeaderContent>
          <div>
            <TitleRow>
              <Title>{constants.SESSION_HISTORY} {constants.BY_DATE}</Title>
              <BetaTag text="These Reports are in BETA as they are generated by our ever-improving AI and may contain inaccuracies. Use it as a helpful guide to supplement your expert observations." />
            </TitleRow>
          </div>
          {weeklyReport && (
            <DownloadButton onClick={handleDownloadPdf} disabled={isDownloading} data-pdf-exclude>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15M7 10L12 15M12 15L17 10M12 15V3" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>{isDownloading ? "Downloading..." : "Download PDF"}</span>
            </DownloadButton>
          )}
        </HeaderContent>
      </PageHeader>

      <ContentArea>
        <FilterBar>
          <FilterGroup>
            <FilterLabel>Class</FilterLabel>
            <SelectDropdown
              ariaLabel="Class"
              value={selectedClassId}
              displayText={classDisplayName(selectedClass) || "Select a class"}
              options={classes.map((cls) => ({ value: cls.id, label: cls.name, school_year: cls.school_year }))}
              groupBySchoolYear
              onChange={setSelectedClassId}
            />
          </FilterGroup>

          <DateFilterDropdown
            label="Date"
            classroomId={selectedClassId}
            educatorId={educatorId}
            startDate={selectedWeek}
            endDate={selectedEndDate}
            selectedDateMode={selectedDateMode}
            onDatesLoaded={handleDatesLoaded}
            onDateChange={(start, end, mode) => {
              setSelectedWeek(start);
              setSelectedEndDate(end);
              setSelectedDateMode(mode);
            }}
          />
        </FilterBar>

        {reportLoading && (
          <LoadingContainer>
            <FontAwesomeIcon icon={faSpinner} spin size="2x" color={colors.blue500} />
          </LoadingContainer>
        )}

        {!reportLoading && !weeklyReport && selectedWeek && (
          <EmptyState>No data for this week</EmptyState>
        )}

        {!reportLoading && !weeklyReport && !selectedWeek && (
          <EmptyState>No weekly reports available for this class yet.</EmptyState>
        )}

        {!reportLoading && weeklyReport && (<>
          <QuickLinks>
            <QuickLinksLabel>Quick links:</QuickLinksLabel>
            <QuickLinkItem type="button" onClick={() => scrollToSection("section-student-stars")}>Student Stars</QuickLinkItem>
            <QuickLinkDivider>|</QuickLinkDivider>
            <QuickLinkItem type="button" onClick={() => scrollToSection("section-collaboration")}>Collaboration</QuickLinkItem>
            <QuickLinkDivider>|</QuickLinkDivider>
            <QuickLinkItem type="button" onClick={() => scrollToSection("section-proficiency")}>Math Proficiency &amp; Standards</QuickLinkItem>
          </QuickLinks>
          <SectionsContainer>
            {/* Activity Summary */}
            <Section id="section-activity-summary">
              <SectionHeader>
                <SectionHeaderLeft>
                  <SectionTitle>Dosage Summary</SectionTitle>
                </SectionHeaderLeft>
                <SectionFeedback
                  section="activity_summary"
                  feedback={weeklyReport.section_feedback?.activity_summary}
                  onFeedback={openFeedbackOverlay}
                />
              </SectionHeader>
              <SummaryCards>
                <SummaryCardLink as="button" type="button" aria-label="Active Students — view student activity" onClick={navigateToStudentActivity}>
                  <SummaryIcon>
                    <img src={iconStudents} alt="" />
                  </SummaryIcon>
                  <SummaryCardContent>
                    <SummaryValue>{weeklyReport.activity_summary?.active_students}</SummaryValue>
                    <SummaryLabel>Active Students</SummaryLabel>
                  </SummaryCardContent>
                </SummaryCardLink>
                <SummaryCardLink as="button" type="button" aria-label="Sessions Completed — view student activity" onClick={navigateToStudentActivity}>
                  <SummaryIcon>
                    <img src={iconSessionsCompleted} alt="" />
                  </SummaryIcon>
                  <SummaryCardContent>
                    <SummaryValue>{weeklyReport.activity_summary?.sessions_completed}</SummaryValue>
                    <SummaryLabel>Sessions Completed</SummaryLabel>
                  </SummaryCardContent>
                </SummaryCardLink>
                <SummaryCardLink as="button" type="button" aria-label="Total Student Dosage — view student activity" onClick={navigateToStudentActivity}>
                  <SummaryIcon>
                    <img src={iconDosageGoal} alt="" />
                  </SummaryIcon>
                  <SummaryCardContent>
                    <SummaryValue>{formatDuration(weeklyReport.activity_summary?.total_minutes)}</SummaryValue>
                    <SummaryLabel>Total Student Dosage</SummaryLabel>
                  </SummaryCardContent>
                </SummaryCardLink>
              </SummaryCards>
            </Section>

            {/* Student Stars */}
            {weeklyReport.student_stars?.filter(s => s.student_name && s.student_name !== "Unknown Student").length > 0 && (
              <StudentStarsSection id="section-student-stars"
                stars={weeklyReport.student_stars.filter(s => s.student_name && s.student_name !== "Unknown Student")}
                feedback={weeklyReport.section_feedback?.student_stars}
                onFeedback={openFeedbackOverlay}
                classId={selectedClassId}
                weekOf={weekOf}
                weekEnd={weekEnd}
              />
            )}
            {weeklyReport.collaboration && (
              <CollaborationSection
                id="section-collaboration"
                collaboration={weeklyReport.collaboration}
                feedback={weeklyReport.section_feedback?.collaboration}
                onFeedback={openFeedbackOverlay}
                selectedClassId={selectedClassId}
                weekOf={weekOf}
                weekEnd={weekEnd}
              />
            )}
            {weeklyReport.proficiency && (
              <ProficiencySection
                id="section-proficiency"
                proficiency={weeklyReport.proficiency}
                questionPerformance={weeklyReport.question_performance}
                feedback={weeklyReport.section_feedback?.proficiency}
                onFeedback={openFeedbackOverlay}
                classId={selectedClassId}
                weekOf={weekOf}
                weekEnd={weekEnd}
              />
            )}
          </SectionsContainer>
          <div data-pdf-exclude>
            <FeedbackCTA
              reportLocation="Weekly Report"
              contextMessage={`[Weekly Report] Class: ${selectedClass?.name || "N/A"} (ID: ${selectedClassId || "N/A"}), Week of: ${selectedWeek || "N/A"}`}
            />
          </div>
          <FeedbackOverlay
            show={feedbackOverlay.show}
            sectionLabel={SECTION_LABELS[feedbackOverlay.section] || ""}
            initialRating={feedbackOverlay.initialRating}
            onSubmit={(isPositive, comment) => {
              handleFeedback(feedbackOverlay.section, isPositive, comment);
            }}
            onClose={() => setFeedbackOverlay({ show: false, section: null })}
          />
        </>)}
      </ContentArea>
    </Container>
  );
};

const formatStarText = (text) => {
  if (!text) return text;
  return text.replace(/^Student Star:\s*/i, "");
};

const StudentStarsSection = ({ stars, feedback, onFeedback, classId, weekOf, weekEnd, id }) => {
  const history = useHistory();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const currentCardRef = useRef(null);
  const nextCardRef = useRef(null);

  const animateSwipe = (newIndex) => {
    if (isAnimating || stars.length <= 1) return;
    setIsAnimating(true);
    setNextIndex(newIndex);

    // Next card starts behind: scaled down, fully visible underneath
    gsap.set(nextCardRef.current, {
      opacity: 1,
      scale: 0.9,
      zIndex: 0,
    });

    const tl = gsap.timeline({
      onComplete: () => {
        setCurrentIndex(newIndex);
        setNextIndex(null);
        setIsAnimating(false);
        if (currentCardRef.current) {
          gsap.set(currentCardRef.current, {
            clearProps: "transform,opacity,zIndex",
          });
        }
      },
    });

    // Lift card up 8px, then fade out
    tl.to(currentCardRef.current, {
      y: -16,
      zIndex: 10,
      boxShadow: "0px 12px 32px rgba(14, 26, 49, 0.22)",
      duration: 0.2,
      ease: "power2.out",
    }, 0);

    tl.to(currentCardRef.current, {
      opacity: 0,
      duration: 0.3,
      ease: "power1.in",
    }, 0.2);

    // Next card: scale from 0.95 to 1 while current card lifts away
    tl.to(nextCardRef.current, {
      scale: 1,
      zIndex: 1,
      duration: 0.6,
      ease: "power2.out",
    }, 0);
  };

  const goToPrevious = () => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : stars.length - 1;
    animateSwipe(newIndex);
  };

  const goToNext = () => {
    const newIndex = currentIndex < stars.length - 1 ? currentIndex + 1 : 0;
    animateSwipe(newIndex);
  };

  const star = stars[currentIndex];
  const nextStar = nextIndex !== null ? stars[nextIndex] : null;
  if (!star) return null;

  return (
    <Section id={id}>
      <SectionHeader>
        <SectionHeaderLeft>
          <SectionTitle>Student Stars</SectionTitle>
        </SectionHeaderLeft>
        <SectionFeedback
          section="student_stars"
          feedback={feedback}
          onFeedback={onFeedback}
        />
      </SectionHeader>
      <CarouselContainer>
        <CarouselButton onClick={goToPrevious} disabled={stars.length <= 1 || isAnimating} aria-label="Previous" data-pdf-exclude>
          <FontAwesomeIcon icon={faChevronLeft} />
        </CarouselButton>
        <CarouselCardWrapper>
          {nextStar && (
            <StarCard ref={nextCardRef} style={{ position: "absolute", inset: 0 }}>
              <StarCardHeader>
                <FontAwesomeIcon icon={faStar} style={{ color: colors.nightlyWoods500, fontSize: "14px" }} />
                <StarStudentName>{nextStar.student_name}</StarStudentName>
              </StarCardHeader>
              <StarText>{formatStarText(nextStar.star_text)}</StarText>
            </StarCard>
          )}
          <StarCard
            ref={currentCardRef}
            style={{ position: "relative", zIndex: 1, cursor: "pointer" }}
            onClick={() => {
              saveFilters("session_history", {
                classroomId: classId,
                ...(weekOf && { startDate: weekOf, endDate: weekEnd }),
              });
              const params = new URLSearchParams();
              if (classId) params.set("classroom_id", classId);
              if (weekOf) params.set("start_date", weekOf);
              if (weekEnd) params.set("end_date", weekEnd);
              if (star.session_id) params.set("session_id", star.session_id);
              if (star.student_name) params.set("highlight_student", star.student_name);
              // Session Reports opens By Lesson by default; this link is about
              // sessions, including when the star names no session of its own.
              params.set("tab", "groups");
              window.open(`${constants.SESSION_HISTORY_PATH}?${params.toString()}`, "_blank");
            }}
          >
            <StarCardHeader>
              <FontAwesomeIcon icon={faStar} style={{ color: colors.nightlyWoods500, fontSize: "14px" }} />
              <StarStudentName>{star.student_name}</StarStudentName>
            </StarCardHeader>
            <StarText>{formatStarText(star.star_text)}</StarText>
          </StarCard>
        </CarouselCardWrapper>
        <CarouselButton onClick={goToNext} disabled={stars.length <= 1 || isAnimating} aria-label="Next" data-pdf-exclude>
          <FontAwesomeIcon icon={faChevronRight} />
        </CarouselButton>
      </CarouselContainer>
    </Section>
  );
};

const ThumbsUpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M7 10V22M15 5.88L14 10H19.83C20.1405 10 20.4467 10.0723 20.7244 10.2111C21.0021 10.35 21.2437 10.5516 21.43 10.8C21.6163 11.0484 21.7422 11.3367 21.7977 11.6422C21.8533 11.9477 21.8369 12.2619 21.75 12.56L19.42 20.56C19.2988 20.9754 19.0462 21.3404 18.7 21.6C18.3538 21.8596 17.9327 22 17.5 22H4C3.46957 22 2.96086 21.7893 2.58579 21.4142C2.21071 21.0391 2 20.5304 2 20V12C2 11.4696 2.21071 10.9609 2.58579 10.5858C2.96086 10.2107 3.46957 10 4 10H6.76C7.13208 9.9998 7.49674 9.89581 7.81296 9.69972C8.12917 9.50363 8.38442 9.22321 8.55 8.89L12 2C12.4716 2.00584 12.9357 2.11817 13.3578 2.3286C13.7799 2.53902 14.1489 2.84211 14.4374 3.2152C14.7259 3.5883 14.9263 4.02176 15.0237 4.4832C15.1212 4.94464 15.113 5.42213 15 5.88Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ThumbsDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M17 14V2M9 18.12L10 14H4.17C3.85951 14 3.55328 13.9277 3.27559 13.7889C2.99789 13.65 2.75629 13.4484 2.57 13.2C2.38371 12.9516 2.25785 12.6633 2.20227 12.3578C2.1467 12.0523 2.16306 11.7381 2.25 11.44L4.58 3.44C4.70117 3.02456 4.95384 2.65961 5.3 2.4C5.64616 2.14035 6.06727 2 6.5 2H20C20.5304 2 21.0391 2.21071 21.4142 2.58579C21.7893 2.96086 22 3.46957 22 4V12C22 12.5304 21.7893 13.0391 21.4142 13.4142C21.0391 13.7893 20.5304 14 20 14H17.24C16.8679 14.0002 16.5033 14.1042 16.187 14.3003C15.8708 14.4964 15.6156 14.7768 15.45 15.11L12 22C11.5284 21.9942 11.0643 21.8818 10.6422 21.6714C10.2201 21.461 9.85106 21.1579 9.56262 20.7848C9.27418 20.4117 9.07368 19.9782 8.97627 19.5168C8.87885 19.0554 8.887 18.5779 9 18.12Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SectionFeedback = ({ section, feedback, onFeedback }) => {
  const isPositive = feedback?.is_positive;
  const hasRating = feedback !== undefined && feedback !== null;

  return (
    <FeedbackButtons data-pdf-exclude>
      <FeedbackButton
        $active={hasRating && isPositive === true}
        onClick={() => onFeedback(section, true)}
        title="Thumbs up"
      >
        <ThumbsUpIcon />
      </FeedbackButton>
      <FeedbackButton
        $active={hasRating && isPositive === false}
        onClick={() => onFeedback(section, false)}
        title="Thumbs down"
      >
        <ThumbsDownIcon />
      </FeedbackButton>
    </FeedbackButtons>
  );
};

// COLLABORATION levels — NOT math proficiency. This map only LOOKS like
// PROFICIENCY_LEVEL_CONFIG below; it measures something else entirely, and the
// two are deliberately kept separate. Do not unify them, and do not "fix" this
// one's colours alongside that one's. Exported so a test can pin that it stays
// as it is.
export const LEVEL_CONFIG = {
  1: {
    bg: colors.pink50,
    text: colors.pink500,
    description: "Needs Intervention",
    BadgeFrame: Bronze,
  },
  2: {
    bg: colors.orange50,
    text: colors.orange500,
    description: "Needs Guidance",
    BadgeFrame: Silver,
  },
  3: {
    bg: colors.green50,
    text: colors.green500,
    description: "Effective Collaborator",
    BadgeFrame: Gold,
  },
  4: {
    bg: colors.blue50,
    text: colors.blue500,
    description: "Exceeding Expectations",
    BadgeFrame: Diamond,
    guidance: "Participation was exceptional, effectively balancing precise mathematical reasoning with genuine social support for peers. Validate this \"collective efficacy\" by publicly highlighting a specific moment where they built on another's idea or supported a stuck teammate. Consider positioning them as a \"Collaboration Captain\" in future groupings to model these high-trust interactions for students who are still developing their social scripts.",
  },
};

const BackToTopButton = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <BackToTopWrapper onClick={scrollToTop} data-pdf-exclude>
      <BackToTopIcon>
        <FontAwesomeIcon icon={faArrowUp} />
      </BackToTopIcon>
      <span>Back to Top</span>
    </BackToTopWrapper>
  );
};

const parseActionText = (text) => {
  if (!text) return { title: null, description: "" };
  const dashIndex = text.indexOf(" — ");
  if (dashIndex !== -1) {
    return {
      title: text.substring(0, dashIndex),
      description: text.substring(dashIndex + 3),
    };
  }
  if (text.startsWith("Action:")) {
    return { title: null, description: text };
  }
  return { title: null, description: text };
};

const CollaborationSection = ({ id, collaboration, feedback, onFeedback, selectedClassId, weekOf, weekEnd }) => {
  const sortedLevels = [...(collaboration.levels || [])].sort(
    (a, b) => b.level - a.level
  );
  const students = collaboration.students || [];

  return (
    <CollabSection id={id}>
      <SectionHeader>
        <SectionHeaderLeft>
          <SectionTitle>Collaboration</SectionTitle>
        </SectionHeaderLeft>
        <SectionFeedback
          section="collaboration"
          feedback={feedback}
          onFeedback={onFeedback}
        />
      </SectionHeader>
      <CollabCardsContainer>
        {sortedLevels.map((level) => {
          const levelStudents = students
            .filter((s) => s.current_level === level.level)
            .sort((a, b) => (a.student_name || "").localeCompare(b.student_name || ""));
          return (
            <CollabBucketCard
              key={level.level}
              level={level}
              students={levelStudents}
              selectedClassId={selectedClassId}
              weekOf={weekOf}
              weekEnd={weekEnd}
            />
          );
        })}
      </CollabCardsContainer>
      <BackToTopButton />
    </CollabSection>
  );
};

export const splitIntoColumns = (items, maxCols = 3, minPerCol = 4) => {
  const perCol = Math.max(minPerCol, Math.ceil(items.length / maxCols));
  return Array.from({ length: maxCols }, (_, i) =>
    items.slice(i * perCol, (i + 1) * perCol)
  ).filter((col) => col.length > 0);
};

const CollabBucketCard = ({ level, students, selectedClassId, weekOf, weekEnd }) => {
  const navigateToStudent = (studentId) => {
    saveFilters("session_history", {
      classroomId: selectedClassId,
      studentId,
      ...(weekOf && { startDate: weekOf, endDate: weekEnd }),
    });
    const params = new URLSearchParams();
    if (selectedClassId) params.set("classroom_id", selectedClassId);
    if (studentId) params.set("student_id", studentId);
    if (weekOf) params.set("start_date", weekOf);
    if (weekEnd) params.set("end_date", weekEnd);
    // Session Reports opens By Lesson by default; this link is about sessions.
    params.set("tab", "groups");
    window.open(`${constants.SESSION_HISTORY_PATH}?${params.toString()}`, "_blank");
  };

  const config = LEVEL_CONFIG[level.level] || LEVEL_CONFIG[1];
  const { title, description } = parseActionText(level.action_text);
  const BadgeFrame = config.BadgeFrame;

  const columns = splitIntoColumns(students);

  return (
    <CollabCard>
      <CollabCardLeft>
        <CollabBadgeWrapper>
          <BadgeFrame />
        </CollabBadgeWrapper>
        <CollabLevelInfo>
          <CollabLevelTag $bg={config.bg} $color={config.text}>
            Level {level.level}: {level.name}
          </CollabLevelTag>
          <CollabLevelDescription>{config.description}</CollabLevelDescription>
        </CollabLevelInfo>
      </CollabCardLeft>
      <CollabCardRight>
        <CollabActionCopy>
          {title && (
            <CollabActionTitle>
              <span>{title.split(":")[0]}:</span>{" "}
              {title.split(":").slice(1).join(":")}
            </CollabActionTitle>
          )}
          {(config.guidance || description) && (
            <CollabActionDescription>{config.guidance || description}</CollabActionDescription>
          )}
        </CollabActionCopy>
        {students.length === 0 ? (
          <CollabNoStudents>No students in this level</CollabNoStudents>
        ) : (
          <CollabStudentGrid>
            {columns.map((col, colIdx) => (
              <CollabStudentColumn key={colIdx}>
                {col.map((student) => (
                  <CollabStudentItem key={student.student_id} type="button" onClick={() => navigateToStudent(student.student_id)}>
                    <CollabStudentNameLink>
                      {student.student_name}
                    </CollabStudentNameLink>
                  </CollabStudentItem>
                ))}
              </CollabStudentColumn>
            ))}
          </CollabStudentGrid>
        )}
      </CollabCardRight>
    </CollabCard>
  );
};

// MATH PROFICIENCY levels — NOT collaboration; see LEVEL_CONFIG above. These
// text colours are the accessible ones: the lighter shades they replaced scored
// 3.38 / 2.89 / 3.15 against these very backgrounds and so failed WCAG AA. The
// backgrounds themselves are unchanged, and lightening the text again would
// reintroduce the failure.
// The same palette the Class Reports proficiency tab draws its cards with.
const PROFICIENCY_LEVEL_CONFIG = {
  1: STATUS_CONFIG.LEVEL_1,
  2: STATUS_CONFIG.LEVEL_2,
  3: STATUS_CONFIG.LEVEL_3,
};

// THE CELL'S IDENTITY IS ITS `status`, NEVER ITS `math_level`.
//
// `math_level` is a projection: it is 1/2/3 on a firm cell and NULL on every
// work-in-progress cell. The WIP column carries no level either. So bucketing a
// student by "does this cell's level equal this column's level" compares one
// absent value with another — every WIP student then lands in a column by
// coincidence rather than by meaning, or, under a stricter comparison, lands in
// no column at all and vanishes from a table that is about them. In a one-week
// report most of the class is work in progress, so that is the majority of the
// table, not an edge case. Bucket on `status`, mapped to the column's `key`.
const WIP_COLUMN_KEY = "WIP";

// THREE summary cards, one per firm level. The WIP column is filtered out
// EXPLICITLY, and the filter has to stay explicit: one loop drives both the
// cards and the header, so a fourth column otherwise produces a fourth card by
// default — and because that card has no level number, the styling lookup would
// miss and draw it in Level 1's pink, wearing Level 1's tag, over an empty
// hexagon. WIP has no number to put in a hexagon and no written action text.
const FIRM_LEVEL_COLUMN_KEYS = ["LEVEL_1", "LEVEL_2", "LEVEL_3"];

// A name's state is carried on screen by its column header, which is visible
// when scanning but not necessarily announced when tabbing from link to link.
// The accessible name says which column the name sits in — and nothing more:
// no level for a work-in-progress cell, and no fraction for anyone.
const columnAnnouncement = (column) =>
  column.key === WIP_COLUMN_KEY ? "work in progress" : column.short_label;

const STATUS_COLUMN_KEY = {
  LEVEL_1: "LEVEL_1",
  LEVEL_2: "LEVEL_2",
  LEVEL_3: "LEVEL_3",
  WIP_HIGH: WIP_COLUMN_KEY,
  WIP_MID: WIP_COLUMN_KEY,
  WIP_LOW: WIP_COLUMN_KEY,
};

export const ProficiencySection = ({ id, proficiency, questionPerformance, feedback, onFeedback, classId, weekOf, weekEnd }) => {
  const levels = proficiency.levels || [];
  const standards = proficiency.standards || [];
  const students = proficiency.students || [];
  const summaryLevels = levels.filter(
    (level) =>
      FIRM_LEVEL_COLUMN_KEYS.includes(level.key) && PROFICIENCY_LEVEL_CONFIG[level.level]
  );
  const qpStudents = questionPerformance?.students || [];
  const [qpTooltip, setQpTooltip] = useState(null);

  const getStudentsForStandardColumn = (standardCode, columnKey) => {
    return students.filter((s) => {
      const stdData = s.standards?.[standardCode];
      return !!stdData && STATUS_COLUMN_KEY[stdData.status] === columnKey;
    });
  };

  const handleStudentHover = (e, studentId) => {
    const qp = qpStudents.find((q) => q.student_id === studentId);
    if (!qp) {
      setQpTooltip(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setQpTooltip({
      studentId,
      lastSession: qp.last_session,
      range: qp.range_summary,
      sessionCount: qp.session_count,
      x: rect.right + 16,
      y: rect.top + rect.height / 2,
    });
  };

  return (
    <ProficiencyWrapper id={id}>
      <SectionHeader>
        <SectionHeaderLeft>
          <SectionTitle>Math Proficiency</SectionTitle>
        </SectionHeaderLeft>
        <SectionFeedback
          section="proficiency"
          feedback={feedback}
          onFeedback={onFeedback}
        />
      </SectionHeader>

      {standards.length === 0 ? (
        <ProficiencyEmptyState>
          <ProficiencyEmptyTitle>No completed solo work in this period</ProficiencyEmptyTitle>
          <ProficiencyEmptyBody>
            Proficiency is measured from questions students answered on their own. This
            class has none for the selected dates.
          </ProficiencyEmptyBody>
        </ProficiencyEmptyState>
      ) : (
        <>
          <ProficiencySummaryContainer>
            <ProficiencySummaryCards>
              {summaryLevels.map((level) => {
                const config = PROFICIENCY_LEVEL_CONFIG[level.level];
                return (
                  <ProficiencySummaryCard key={level.key}>
                    <ProficiencyLevelBadge level={level.level} color={config.color} />
                    <ProficiencySummaryTagRow>
                      <ProficiencyLevelTag $bg={config.bg} $color={config.color} $border={config.border}>
                        {config.label}
                      </ProficiencyLevelTag>
                      <ProficiencySummaryLabel>{level.label}</ProficiencySummaryLabel>
                    </ProficiencySummaryTagRow>
                    <ProficiencySummaryAction>
                      <span>Action: </span>
                      {level.action_text}
                    </ProficiencySummaryAction>
                  </ProficiencySummaryCard>
                );
              })}
            </ProficiencySummaryCards>
          </ProficiencySummaryContainer>

          <ProficiencyTable>
            <ProficiencyTableHeader>
              <ProficiencyTableHeaderCell $width="268px">Standard</ProficiencyTableHeaderCell>
              {levels.map((level) => (
                <ProficiencyTableHeaderCell key={level.key} $flex>
                  {level.short_label}
                </ProficiencyTableHeaderCell>
              ))}
            </ProficiencyTableHeader>
            {standards.map((standard) => (
              <ProficiencyTableRow key={standard.standard_code}>
                <ProficiencyTableStandardCell>
                  <ProficiencyStandardCode>{standard.standard_code}</ProficiencyStandardCode>
                  <ProficiencyStandardName>{standard.standard_name}</ProficiencyStandardName>
                  {standard.completed_solo_answers != null && (
                    <ProficiencyStandardCount>
                      {standard.completed_solo_answers} completed solo answers
                    </ProficiencyStandardCount>
                  )}
                </ProficiencyTableStandardCell>
                {levels.map((level) => {
                  const levelStudents = getStudentsForStandardColumn(
                    standard.standard_code,
                    level.key
                  );
                  return (
                    <ProficiencyTableCell key={level.key}>
                      {levelStudents.map((s) => {
                        const params = new URLSearchParams();
                        if (s.student_id) params.set("student_id", s.student_id);
                        if (classId) params.set("classroom_id", classId);
                        if (standard.standard_code) {
                          const codes = standard.standard_code.split(",").map(c => c.trim());
                          if (codes.length === 1) {
                            params.set("standard_code", codes[0]);
                          } else {
                            const parts = codes.map(c => c.split("."));
                            let common = [];
                            for (let i = 0; i < parts[0].length; i++) {
                              if (parts.every(p => p[i] === parts[0][i])) {
                                common.push(parts[0][i]);
                              } else break;
                            }
                            params.set("standard_code", common.join("."));
                          }
                        }
                        if (weekOf) params.set("start_date", weekOf);
                        if (weekEnd) params.set("end_date", weekEnd);
                        // Session Reports opens By Lesson by default; this
                        // link is about sessions.
                        params.set("tab", "groups");
                        return (
                          <ProficiencyStudentLink
                            key={s.student_id}
                            aria-label={`${s.student_name}, ${standard.standard_code}: ${columnAnnouncement(level)}`}
                            href={`${constants.SESSION_HISTORY_PATH}?${params.toString()}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onMouseEnter={(e) => handleStudentHover(e, s.student_id)}
                            onMouseLeave={() => setQpTooltip(null)}
                          >
                            {s.student_name}
                          </ProficiencyStudentLink>
                        );
                      })}
                    </ProficiencyTableCell>
                  );
                })}
              </ProficiencyTableRow>
            ))}
          </ProficiencyTable>
        </>
      )}
      {qpTooltip && (
        <QpTooltip
          style={{
            left: qpTooltip.x,
            top: qpTooltip.y,
            transform: "translateY(-50%)",
          }}
        >
          {qpTooltip.lastSession && (
            <QpTooltipSection>
              <QpTooltipLabel>Last Session</QpTooltipLabel>
              <QpTooltipValue>
                Correct: {qpTooltip.lastSession.correct}/{qpTooltip.lastSession.total}, {qpTooltip.lastSession.minutes} Min
              </QpTooltipValue>
            </QpTooltipSection>
          )}
          <QpTooltipSection>
            <QpTooltipLabel>
              This Week ({qpTooltip.sessionCount} {qpTooltip.sessionCount === 1 ? "Session" : "Sessions"})
            </QpTooltipLabel>
            <QpTooltipValue>
              Correct: {qpTooltip.range.correct}/{qpTooltip.range.total}, {qpTooltip.range.minutes} Min
            </QpTooltipValue>
          </QpTooltipSection>
        </QpTooltip>
      )}
      <BackToTopButton />
    </ProficiencyWrapper>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 92px);
  background-color: ${colors.milkGlass200};
`;

const PageHeader = styled.div`
  background-color: ${colors.nightlyWoods500};
  padding: 24px 0;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px 0 ${pageTitleInset};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.h1`
  font-family: "Plus Jakarta Sans", sans-serif;
  font-weight: 700;
  font-size: 40px;
  line-height: 44px;
  color: ${colors.milkGlass500};
  margin: 0;
`;

const Subtitle = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  color: #FFFEFE;
  margin: 4px 0 0 0;
`;

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: ${colors.gray50};
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  padding: 8px;

  &:hover {
    opacity: 0.8;
  }
`;

const ContentArea = styled.div`
  padding: 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const QuickLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 16px;
`;

const QuickLinksLabel = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
  white-space: nowrap;
`;

const QuickLinkItem = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
  text-decoration: underline;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: ${colors.blue500};
  }
`;

const QuickLinkDivider = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 16px;
  line-height: 22px;
  color: #cbcdcc;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 24px;
  background: transparent;
  padding: 2rem 16px 0;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  max-width: 300px;
`;

const FilterLabel = styled.label`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: black;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px;
`;

const ErrorContainer = styled.div`
  padding: 32px;
  text-align: center;
  color: ${colors.orange600};
  font-size: 16px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px;
  color: ${colors.milkGlass950};
  font-size: 16px;
  font-family: "Inter", sans-serif;
`;

const SectionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-top: 40px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const SectionHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackToTopIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1.5px solid #002E19;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
`;

const BackToTopWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  color: #002E19;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
`;

const SectionTitle = styled.h2`
  font-family: "ABC Marfa", "Inter", sans-serif;
  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  color: ${colors.nightlyWoods500};
  margin: 0;
`;

const FeedbackButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
`;

const FeedbackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: ${(props) => (props.$active ? colors.blue500 : "#002E19")};
  display: flex;
  align-items: center;

  &:hover {
    color: ${colors.blue500};
  }
`;

const SummaryCards = styled.div`
  display: flex;
  justify-content: space-evenly;
  gap: 1rem;
  width: 100%;
`;

const SummaryCard = styled.div`
  max-width: 260px;
  width: 100%;
  max-width: 300px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  background: white;
  border-radius: 16px;
  padding: 1rem;
  box-shadow: 0px 1px 3px 0px rgba(14, 26, 49, 0.1), 0px 1px 2px 0px rgba(14, 26, 49, 0.06);
`;

const SummaryCardLink = styled(SummaryCard)`
  border: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: box-shadow 0.2s ease;
  &:hover {
    box-shadow: 0px 4px 8px 0px rgba(14, 26, 49, 0.15), 0px 2px 4px 0px rgba(14, 26, 49, 0.1);
  }
`;

const SummaryIcon = styled.div`
  width: 62px;
  height: 62px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 62px;
    height: 62px;
    object-fit: contain;
  }
`;

const SummaryCardContent = styled.div`
  display: flex;
  flex-direction: column;
  color: ${colors.nightlyWoods500};
`;

const SummaryValue = styled.span`
  font-family: "ABC Marfa", "Inter", sans-serif;
  font-weight: 600;
  font-size: 32px;
  line-height: 32px;
  letter-spacing: 0.25px;
`;

const SummaryLabel = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
`;

const CarouselContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
`;

const CarouselCardWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const CarouselButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 100px;
  border: 1px solid ${colors.nightlyWoods500};
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  color: ${colors.nightlyWoods500};
  font-size: 14px;
  opacity: ${(props) => (props.disabled ? 0.3 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};

  &:hover {
    background: ${colors.milkGlass200};
  }

  &:active {
    opacity: 0.5;
  }
`;

const StarCard = styled.div`
  flex: 1;
  background: white;
  border-radius: 12px;
  padding: 32px;
  min-height: 166px;
  box-shadow: 0px 4px 12px rgba(14, 26, 49, 0.1), 0px 1px 3px rgba(14, 26, 49, 0.06);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StarCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StarStudentName = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 26px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
`;

const StarText = styled.p`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.nightlyWoods500};
  margin: 0;
`;

const CollabSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 1rem;
`;

const CollabCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CollabCard = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0px 1px 3px 0px rgba(14, 26, 49, 0.1),
    0px 1px 2px 0px rgba(14, 26, 49, 0.06);
`;

const CollabCardLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 190px;
  flex-shrink: 0;
`;

const CollabBadgeWrapper = styled.div`
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  & > svg {
    width: 120px;
    height: auto;
  }
`;

const CollabLevelInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const CollabLevelTag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 11px;
  background-color: ${(props) => props.$bg};
  color: ${(props) => props.$color};
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  white-space: nowrap;
`;

const CollabLevelDescription = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
  text-align: center;
`;

const CollabCardRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-width: 0;
`;

const CollabActionCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${colors.nightlyWoods100 || "#cbcdcc"};
  color: ${colors.nightlyWoods500};
`;

const CollabActionTitle = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
  margin: 0;

  span {
    font-weight: 700;
  }
`;

const CollabActionDescription = styled.p`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.nightlyWoods500};
  margin: 0;
`;

const CollabStudentsHeading = styled.p`
  font-family: "Inter", sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
  margin: 0;
`;

const CollabNoStudents = styled.p`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.nightlyWoods500};
  margin: 0;
`;

const CollabStudentGrid = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
`;

const CollabStudentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-width: 0;
`;

const CollabStudentItem = styled.button`
  background: none;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const CollabStudentName = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CollabStudentNameLink = styled(CollabStudentName)`
  &:hover {
    text-decoration: underline;
    color: ${colors.blue500};
  }
`;

const ProficiencyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-top: 1rem;
`;

const ProficiencySummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ProficiencySummarySubtitle = styled.h3`
  font-family: "ABC Marfa", "Inter", sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 26px;
  color: ${colors.nightlyWoods500};
  margin: 0;
`;

const ProficiencySummaryCards = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
`;

const ProficiencySummaryCard = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0px 1px 3px 0px rgba(14, 26, 49, 0.1),
    0px 1px 2px 0px rgba(14, 26, 49, 0.06);
`;

const ProficiencyLevelTag = styled.span`
  background-color: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  ${(p) => (p.$border ? `border: 1px solid ${p.$border};` : "")}
  border-radius: 11px;
  padding: 4px 8px;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  white-space: nowrap;
`;

const ProficiencySummaryTagRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ProficiencySummaryLabel = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
  text-align: center;
`;

const ProficiencySummaryAction = styled.div`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
  border-top: 1px solid ${colors.nightlyWoods100 || "#cbcdcc"};
  padding-top: 16px;
  width: 100%;

  span {
    font-weight: 700;
  }
`;

const ProficiencyEmptyState = styled.div`
  width: 100%;
  background-color: #fffefe;
  border: 1px solid ${colors.nightlyWoods100 || "#e7e7e7"};
  border-radius: 8px;
  padding: 40px 24px;
  text-align: center;
`;

const ProficiencyEmptyTitle = styled.strong`
  display: block;
  font-family: "Inter", sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
  margin-bottom: 6px;
`;

const ProficiencyEmptyBody = styled.p`
  margin: 0;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: ${colors.milkGlass950};
`;

const ProficiencyTable = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ProficiencyTableHeader = styled.div`
  display: flex;
  width: 100%;
`;

const ProficiencyTableHeaderCell = styled.div`
  background-color: ${colors.nightlyWoods500};
  color: white;
  font-family: "Inter", sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  padding: 16px;
  ${(props) => (props.$width ? `width: ${props.$width}; flex-shrink: 0;` : "flex: 1;")}
`;

const ProficiencyTableRow = styled.div`
  display: flex;
  width: 100%;
`;

const ProficiencyTableStandardCell = styled.div`
  width: 268px;
  min-height: 144px;
  flex-shrink: 0;
  padding: 16px;
  background-color: #fffefe;
  border-bottom: 1px solid #858b87;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
`;

const ProficiencyStandardCode = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${colors.blue500};
`;

const ProficiencyStandardName = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
`;

const ProficiencyStandardCount = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.25px;
  color: ${colors.milkGlass950};
  margin-top: 2px;
`;

const ProficiencyTableCell = styled.div`
  flex: 1;
  min-height: 144px;
  padding: 16px;
  background-color: #fffefe;
  border-bottom: 1px solid #858b87;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProficiencyStudentName = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
  white-space: nowrap;
`;

const ProficiencyStudentLink = styled.a`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
  width: fit-content;
  &:hover {
    color: ${colors.blue500};
    text-decoration: underline;
  }
`;

const QpTooltip = styled.div`
  position: fixed;
  background-color: ${colors.blue100};
  color: #00140b;
  font-family: "Inter", sans-serif;
  padding: 16px;
  border-radius: 20px;
  pointer-events: none;
  z-index: 2000;
  white-space: nowrap;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &::before {
    content: "";
    position: absolute;
    left: -16px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 12px;
    background-color: ${colors.blue100};
    clip-path: polygon(100% 0, 0 50%, 100% 100%);
  }
`;

const QpTooltipSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const QpTooltipLabel = styled.span`
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
`;

const QpTooltipValue = styled.span`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
`;

